import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  Input,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ACTION_COL,
  AFTER_COL,
  BEFORE_COL,
  BeforeAfterLegend,
} from "./beforeAfter";
import { dependencyItemProblem } from "./pending";

const { Text } = Typography;

/*
 * Shared machinery of the dependency steps (« 3. Adresses », « 4. Responsables »).
 * Every record of the dependency is a row with three columns — before, after, action — and
 * one decision object `{ action: "keep"|"merge", into?, edit? }` that is always sent WHOLE
 * (`onChange({ [key]: { [id]: decision } })`): the wizard replaces `decisions[key][id]` as one piece.
 * Near-duplicate groups (server detection) require an explicit decision for every member.
 */

export const plural = (n, one, many) => `${n || 0} ${n > 1 ? many : one}`;

/** The name shown as the « after » owner: the name typed on step 0/1, or the existing target. */
export const targetNameOf = (decisions, preview) =>
  (decisions && decisions.targetName) ||
  (preview && preview.existingTarget && preview.existingTarget.name) ||
  "l'institution résultante";

/** Ids of the cluster (sources + existing target): whatever points at them ends up on the target. */
export const clusterIds = preview => {
  const p = preview || {};
  const ids = new Set((p.sources || []).map(s => String(s._id)));
  if (p.existingTarget) ids.add(String(p.existingTarget._id));
  return ids;
};

export const hasEdit = d => !!(d && d.edit) && Object.keys(d.edit).length > 0;

const str = v => (v === undefined || v === null ? "" : String(v));

/**
 * State of one row: "pending" (the server readiness would refuse it — same helper as the step
 * counters, steps/pending.js), "decided" (explicit and valid), "default" (nothing chosen,
 * nothing required → keep).
 */
export const rowState = (item, ctx) => {
  if (dependencyItemProblem(ctx, item)) return "pending";
  const d = ctx.decs[item._id];
  return d && d.action ? "decided" : "default";
};

export const StateTag = ({ state }) =>
  state === "pending" ? (
    <Tag color="red">à choisir</Tag>
  ) : state === "decided" ? (
    <Tag color="green">décidé</Tag>
  ) : (
    <Tag>par défaut</Tag>
  );

/** Text saved on blur / Enter — one PUT per edit, never per keystroke. */
function DraftInput({ label, value, disabled, onSave }) {
  const [draft, setDraft] = useState(str(value));
  useEffect(() => setDraft(str(value)), [value]);
  const commit = () => {
    const v = draft.trim();
    if (v !== str(value)) onSave(v);
  };
  return (
    <Input
      size="small"
      disabled={disabled}
      aria-label={label}
      placeholder={label}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={e => e.target.blur()}
    />
  );
}

/**
 * Inline edit form: one control per field (`{ key, label, kind: "text"|"boolean" }`).
 * `edit` only holds the fields that differ from the record: typing the original value back
 * removes the key; the saved object is always the complete `edit`.
 */
export function EditForm({ fields, original, edit = {}, disabled, onSave }) {
  const current = k => (k in edit ? edit[k] : original[k]);
  const save = (f, v) => {
    const next = { ...edit };
    const orig =
      f.kind === "boolean" ? !!original[f.key] : str(original[f.key]);
    if (v === orig) delete next[f.key];
    else next[f.key] = v;
    onSave(next);
  };
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "120px minmax(0, 1fr)",
        gap: "4px 8px",
        alignItems: "center",
        marginTop: 6,
      }}
    >
      {fields.map(f => (
        <React.Fragment key={f.key}>
          <Text type="secondary">{f.label}</Text>
          {f.kind === "boolean" ? (
            <Switch
              size="small"
              disabled={disabled}
              aria-label={f.label}
              checked={!!current(f.key)}
              checkedChildren="oui"
              unCheckedChildren="non"
              onChange={b => save(f, b)}
            />
          ) : (
            <DraftInput
              label={f.label}
              value={current(f.key)}
              disabled={disabled}
              onSave={v => save(f, v)}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const showEdited = (f, v) =>
  f.kind === "boolean" ? (
    !!v ? (
      "oui"
    ) : (
      "non"
    )
  ) : str(v) === "" ? (
    <Text type="secondary">vide</Text>
  ) : (
    str(v)
  );

/** `label : avant → après` for every edited field (strikethrough → bold). */
export function EditDiff({ fields, original, edit = {} }) {
  const keys = Object.keys(edit);
  if (!keys.length) return null;
  return (
    <Space direction="vertical" size={0}>
      {keys.map(k => {
        const f = fields.find(x => x.key === k) || { key: k, label: k };
        return (
          <Text key={k}>
            {f.label} :{" "}
            <Text delete type="secondary">
              {showEdited(f, original[k])}
            </Text>{" "}
            → <Text strong>{showEdited(f, edit[k])}</Text>
          </Text>
        );
      })}
    </Space>
  );
}

/**
 * The « Action » cell: keep / merge into → Select / edit → inline form. « Modifier » is
 * `action: "keep"` + `edit` (an edited survivor); « Rattacher telle quelle » clears the edit.
 */
export function ActionControl({
  item,
  d = {},
  spec,
  candidates,
  map,
  required,
  foreign,
  disabled,
  onSet,
}) {
  const edited = hasEdit(d);
  const [editOpen, setEditOpen] = useState(edited);
  useEffect(() => {
    if (edited) setEditOpen(true);
  }, [edited]);
  // owned by an institution outside the merge: deleting or editing it would alter someone else's data
  if (foreign) {
    const stale = d.action === "merge" || edited;
    return (
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <Radio.Group
          disabled={disabled}
          value={stale || (required && d.action !== "keep") ? null : "keep"}
          onChange={() => onSet({ action: "keep" })}
        >
          <Radio value="keep">{spec.radioKeep}</Radio>
        </Radio.Group>
        <Text type="secondary">
          Adresse d'un autre établissement (hors fusion) : elle reste à son
          propriétaire, seul l'accès est transféré.
        </Text>
        {stale && (
          <Text type="danger">
            Une fusion ou une modification a été enregistrée pour cette adresse
            : choisissez « {spec.radioKeep} ».
          </Text>
        )}
      </Space>
    );
  }
  const value =
    d.action === "merge"
      ? "merge"
      : editOpen || (d.action === "keep" && edited)
      ? "edit"
      : d.action === "keep"
      ? "keep"
      : required
      ? null
      : "keep";
  const pick = v => {
    if (v === "edit") {
      setEditOpen(true);
      onSet({ action: "keep", edit: d.edit || {} });
      return;
    }
    setEditOpen(false);
    if (v === "keep") onSet({ action: "keep" });
    else onSet({ action: "merge", into: d.into });
  };
  const fields = spec.editFields(item);
  const chain =
    d.action === "merge" &&
    d.into &&
    map[d.into] &&
    map[d.into].action === "merge";
  const options = candidates.map(c => {
    const merged = map[c._id] && map[c._id].action === "merge";
    const base = `${spec.labelOf(c)} — ${spec.ownerOf(c)}`;
    return {
      value: c._id,
      disabled: merged,
      label: merged ? `${base} · ${spec.alreadyMerged}` : base,
    };
  });

  return (
    <Radio.Group
      disabled={disabled}
      value={value}
      onChange={e => pick(e.target.value)}
      style={{ width: "100%" }}
    >
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <Radio value="keep">{spec.radioKeep}</Radio>
        <Radio value="merge">{spec.radioMerge}</Radio>
        {value === "merge" && (
          <div style={{ paddingLeft: 24 }}>
            <Select
              showSearch
              disabled={disabled}
              style={{ width: "100%" }}
              placeholder={spec.selectPlaceholder}
              optionFilterProp="label"
              value={d.into || undefined}
              onChange={into => onSet({ action: "merge", into })}
              options={options}
              notFoundContent={spec.noCandidate}
            />
            {chain && <Text type="danger">{spec.chainNote}</Text>}
          </div>
        )}
        <Radio value="edit">{spec.radioEdit}</Radio>
        {value === "edit" && (
          <div style={{ paddingLeft: 24 }}>
            <EditForm
              fields={fields}
              original={item}
              edit={d.edit}
              disabled={disabled}
              onSave={edit => onSet({ action: "keep", edit })}
            />
          </div>
        )}
      </Space>
    </Radio.Group>
  );
}

/**
 * The whole step: Alert, one Card per near-duplicate group, then the « Autres … » table.
 * `spec` describes the dependency (labels, before/after renderers, edit fields, candidates).
 */
export function DependencyStep({
  spec,
  preview,
  decisions,
  onChange,
  disabled,
}) {
  const section = (preview && preview[spec.key]) || {};
  const items = Array.isArray(section.items)
    ? section.items
    : spec.fallbackItems
    ? spec.fallbackItems(section)
    : [];
  const groups = Array.isArray(section.groups) ? section.groups : [];
  const map = (decisions && decisions[spec.key]) || {};
  const byId = new Map(items.map(i => [i._id, i]));
  const groupOf = new Map();
  for (const g of groups)
    for (const id of g[spec.idsKey] || []) groupOf.set(id, g);
  const ctx = {
    targetName: targetNameOf(decisions, preview),
    cluster: clusterIds(preview),
    byId,
    map,
    labelOf: spec.labelOf,
  };

  if (!items.length)
    return <Alert type="success" showIcon message={spec.empty} />;

  const set = (id, decision) => onChange({ [spec.key]: { [id]: decision } });
  const candidatesOf = item => {
    const g = groupOf.get(item._id);
    const list = g
      ? (g[spec.idsKey] || []).map(id => byId.get(id)).filter(Boolean)
      : spec.candidates(item, items);
    return list.filter(c => c._id !== item._id);
  };
  const stateOf = item => {
    return rowState(item, {
      kind: spec.key,
      groupOf,
      byId,
      decs: map,
    });
  };

  const columns = group => [
    {
      title: spec.beforeTitle,
      key: "before",
      width: "34%",
      ...BEFORE_COL,
      render: (_, item) => (
        <Space direction="vertical" size={2}>
          <Space size={6} wrap>
            <StateTag state={stateOf(item)} />
            {group && group.suggestedSurvivor === item._id && (
              <Tag>suggestion</Tag>
            )}
          </Space>
          {spec.renderBefore(item, ctx)}
        </Space>
      ),
    },
    {
      title: "Après",
      key: "after",
      width: "33%",
      ...AFTER_COL,
      render: (_, item) => spec.renderAfter(item, map[item._id] || {}, ctx),
    },
    {
      title: "Action",
      key: "action",
      width: "33%",
      ...ACTION_COL,
      render: (_, item) => (
        <ActionControl
          item={item}
          d={map[item._id]}
          spec={spec}
          candidates={candidatesOf(item)}
          map={map}
          required={groupOf.has(item._id)}
          foreign={!!(spec.isForeign && spec.isForeign(item))}
          disabled={disabled}
          onSet={decision => set(item._id, decision)}
        />
      ),
    },
  ];

  const others = items.filter(i => !groupOf.has(i._id));

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message={spec.alert}
        description={spec.note}
      />
      <BeforeAfterLegend />
      {groups.map(g => {
        const members = (g[spec.idsKey] || [])
          .map(id => byId.get(id))
          .filter(Boolean);
        const decided =
          members.length > 0 && members.every(m => stateOf(m) === "decided");
        return (
          <Card
            key={g.key}
            size="small"
            title={
              <Space wrap>
                <Text>{spec.groupTitle(g, members.length)}</Text>
                <Tag color={decided ? "green" : "red"}>
                  {decided ? "décidé" : "à choisir"}
                </Tag>
              </Space>
            }
          >
            <Table
              size="small"
              pagination={false}
              rowKey="_id"
              columns={columns(g)}
              dataSource={members}
            />
          </Card>
        );
      })}
      <Card size="small" title={spec.othersTitle(others.length)}>
        {others.length ? (
          <Table
            size="small"
            pagination={others.length > 20 ? { pageSize: 20 } : false}
            rowKey="_id"
            columns={columns(null)}
            dataSource={others}
          />
        ) : (
          <Text type="secondary">{spec.noOthers}</Text>
        )}
      </Card>
    </Space>
  );
}
